import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dataDownloaderWorker from "workers/cip-exports-csv-worker";
import WebWorker from "workers/WebWorker";

import { getAnalyticsData } from "api/analytics";

import ChartUtil from "utils/ChartUtil";
import getLogger from "utils/logger.util";
import { getFileNameDateTime } from "utils/utils.js";
import {
  AnalyticsStrings,
  CHART_DOWNLOAD_DATA_DIALOG_STRINGS,
  CIPAnalyticsCleaningTableStrings,
  CIPAnalyticsIndexTableStrings
} from "constants/constantStrings";
import { EXPORT_FILE_TYPE } from "constants/globalConstants";

import createNotification, { INFO_NOTIFICATION, SUCCESS_NOTIFICATION } from "components/Notifications";

const Logger = getLogger("Analytics - DownloadHanlder");

const getTagsDataSheetColumns = tagsAsObj => [
  {
    header: "Date-Time",
    key: "Time",
    width: 20
  },
  {
    header: getHeader("Normalized_Differential_Pressure", tagsAsObj),
    key: "normalized_differential_pressure",
    width: 33
  },
  {
    header: getHeader("Normalized_Permeate_Flow", tagsAsObj),
    key: "normalized_permeate_flow",
    width: 28
  },
  {
    header: getHeader("Normalized_Salt_Passage", tagsAsObj),
    key: "normalized_salt_passage",
    width: 24
  }
];

const getHeader = (tagId, tagsAsObj) => {
  const item = tagsAsObj[tagId.toLocaleLowerCase()];
  return `${item?.TagName}(${item?.Units})`;
};

//Save Excel file
const saveExcelFile = async workbook => {
  const fileName = `CIP_Analytics_${getFileNameDateTime()}.xlsx`;
  const buffer = await workbook.xlsx.writeBuffer();
  const excelData = new Blob([buffer], { type: EXPORT_FILE_TYPE });
  saveAs(excelData, fileName);
  const { downloadSuccessful, downloadSuccessfulMsg } = CHART_DOWNLOAD_DATA_DIALOG_STRINGS;
  createNotification(SUCCESS_NOTIFICATION, downloadSuccessfulMsg.replace("[fileName]", fileName), downloadSuccessful);
};

const createNormalizedTagExcelSheet = (workbook, sheetData, excelTitle, tagsAsObj) => {
  const excelSheet = workbook.addWorksheet("Tags Data");
  const columns = getTagsDataSheetColumns(tagsAsObj);
  excelSheet.columns = columns;
  excelSheet.getRow(1).font = { bold: true };
  excelSheet.getRow(1).alignment = { wrapText: true };

  const descriptionRow = columns.reduce((result, { key }) => {
    const tag = tagsAsObj[key.toLocaleLowerCase()];
    result[key] = tag ? (tag.Description ? `(${tag.Description})` : "(description - N/A)") : "";
    return result;
  }, {});
  excelSheet.addRow(descriptionRow);
  excelSheet.getRow(2).font = { bold: true };
  excelSheet.getRow(2).alignment = { wrapText: true };

  excelSheet.addRows(sheetData);

  ChartUtil.getExcelFileTitle(excelSheet, excelTitle);
};

const createCleaningRequiredExcelSheet = (workbook, sheetData, excelTitle, tagsAsObj) => {
  const updatedSheetData = sheetData?.map(item => {
    const { TagName, Units } = tagsAsObj[item.Tag];
    return { ...item, Tag: `${TagName} (${Units})` };
  });

  const excelSheet = workbook.addWorksheet(AnalyticsStrings.CIP_Cleaning_Required);
  excelSheet.columns = Object.entries(CIPAnalyticsCleaningTableStrings).map(([key, header]) => ({ key, header, width: 20 }));
  excelSheet.getRow(1).font = { bold: true };
  excelSheet.addRows(updatedSheetData);

  ChartUtil.getExcelFileTitle(excelSheet, excelTitle);
};

const createCIPEventsExcelSheet = (workbook, sheetData, excelTitle) => {
  const columns = Object.entries(CIPAnalyticsIndexTableStrings).map(([key, header]) => ({ key, header, width: 20 }));
  for (const sheetName in sheetData) {
    const excelSheet = workbook.addWorksheet(sheetName);
    excelSheet.columns = columns;
    excelSheet.getRow(1).font = { bold: true };
    excelSheet.addRows(sheetData[sheetName]);

    ChartUtil.getExcelFileTitle(excelSheet, excelTitle);
  }
};

export const downloadCipData = async (PlotData, excelTitle, tagsAsObj) => {
  const worker = new WebWorker(dataDownloaderWorker);
  worker.postMessage(PlotData);
  worker.onerror = error => Logger.log("downloadCipData - Error in Worker:", error);
  worker.onmessage = event => {
    Logger.log("downloadCipData... Formatted data from worker: ", event.data);
    const { tagsSheetData, cleaningRequiredSheetsData, cipIndexSheetsData } = event.data;

    const workbook = new ExcelJS.Workbook();

    createNormalizedTagExcelSheet(workbook, tagsSheetData, excelTitle, tagsAsObj);
    createCleaningRequiredExcelSheet(workbook, cleaningRequiredSheetsData, excelTitle, tagsAsObj);
    createCIPEventsExcelSheet(workbook, cipIndexSheetsData, excelTitle);

    saveExcelFile(workbook);
    worker.terminate();
  };
};

export const fetchAndDownloadCIPData = async (group, DurationRanges, excelTitle, tagsAsObj) => {
  Logger.log("fetchAndDownloadCIPData - group: ", group);
  const { downloadStarted, downloadStartedMsg } = CHART_DOWNLOAD_DATA_DIALOG_STRINGS;
  createNotification(INFO_NOTIFICATION, downloadStartedMsg, downloadStarted);
  const startTime = performance.now(); // For debugging only
  const payload = { ...DurationRanges, group };
  const { data } = await getAnalyticsData(payload);
  const endTime = performance.now(); // For debugging only
  Logger.info(`Time took to get CIP data with ${payload.group} seconds interval is ${(endTime - startTime) / 1000} seconds`);
  downloadCipData(data, excelTitle, tagsAsObj);
};

export default { downloadCipData, fetchAndDownloadCIPData };
